import React, { useState, useRef, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Container } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useCareLabelState } from './context/index';
import { checkArray, imageUrl } from '../../../Components/helper';
import SymbolTable from './tables/SymbolTable';
import ErrorLogModal from './ErrorLogModal';
import ImportModal from './ImportModal';
import InstructionDisclaimerTable from './tables/InstructionDisclaimerTable';
import ConfirmationPopup from '../../../Components/ModalPopup/ConfirmationPopup';
import LibraryFilter from './LibraryFilter';
import * as action from './store/CareLabel.action';
import * as userAction from '../../UserSetting/store/User.action';
import { useComponent } from '../../UserSetting/UsersLibrary/UserRights';
import './CareLabel.css';

function List() {
  const dispatch = useDispatch();
  const { careType } = useParams();
  const {
    careLabelState,
    setCareLabelState,
    getCurrentLibraryData,
    handleCheckAll,
    isallChecked,
    userInfo,
    handleAddLibrary,
    handleLibrarySearchText,
    handleSearchSubmit,
  } = useCareLabelState();
  const { careTypeTabs, activeCareType, selectedLibrary, pageType, libSearchText } = careLabelState;
  const reducerState = useSelector((redState) => redState);
  const { Users } = reducerState;
  const { logInRights } = Users;
  const [state, setState] = useState({
    errorLog: [],
    isErrorShow: false,
    deletedId: null,
    deleteModal: false,
    deleteMultipleModal: false,
    importModal: false,
    loader: false,
  });
  const { errorLog, isErrorShow, deletedId, deleteModal, deleteMultipleModal, importModal, loader } = state;
  const { CheckActionPermission } = useComponent();
  const CareAddEditAccess = CheckActionPermission(logInRights, 'libraries', 'care', 'addEdit');
  const CareDeleteAccess = CheckActionPermission(logInRights, 'libraries', 'care', 'delete');

  const docRef = useRef(null);
  const { t } = useTranslation('common');

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('loggedInUserInfo'));
    if (user?.role === 'User') {
      console.log('login Rights', logInRights);
      dispatch(userAction.fetchLogInRights({ section: 'all' }));
    }
    return () => {
      dispatch(action.unmountCareLibrary());
    };
  }, []);

  const importSampleFile = () => {
    setState((prevState) => ({
      ...prevState,
      importModal: true,
    }));
  };

  //  Close Error Modal
  const handleCloseError = () => {
    setState((prevState) => ({
      ...prevState,
      errorLog: [],
      isErrorShow: false,
    }));
  };

  const handleDelete = (id) => {
    setState((prevState) => ({
      ...prevState,
      deletedId: id,
      deleteModal: true,
    }));
  };

  const handleCloseDeleteModal = () => {
    setState((prevState) => ({
      ...prevState,
      deletedId: null,
      deleteModal: false,
      deleteMultipleModal: false,
    }));
  };

  const confirmDelete = async () => {
    const response = await dispatch(action.deleteCareLibrary(deletedId, t));
    if (response.code === 200) {
      setCareLabelState((prevState) => ({
        ...prevState,
        CareLabelList: [],
        selectedLibrary: [],
        pageIndex: 1,
        hasMore: false,
        totalCount: 0,
      }));
      handleCloseDeleteModal();
      getCurrentLibraryData();
    }
  };

  const handleDeleteMultiple = () => {
    setState((prevState) => ({
      ...prevState,
      deleteMultipleModal: true,
    }));
  };

  const confirmDeleteMultiple = async () => {
    const objDeleteMulti = {
      careLibId: 'deleteMultiple',
      loggedInUserId: userInfo?.userId,
      multipleDelete: true,
      careLibIds: selectedLibrary,
    };
    if (checkArray(selectedLibrary)) {
      const response = await dispatch(action.deleteMultiCareLibrary(objDeleteMulti, t));
      if (response?.code === 200) {
        handleCloseDeleteModal();
        setCareLabelState((prevState) => ({
          ...prevState,
          CareLabelList: [],
          selectedLibrary: [],
          pageIndex: 1,
          hasMore: false,
          totalCount: 0,
        }));
        getCurrentLibraryData();
      } else {
        handleCloseDeleteModal();
      }
    }
  };

  const downloadSampleFile = () => {
    const fileUrl = `${imageUrl}/sample-files/Symbol-Sample-file.xlsx`; // Replace with the actual file URL

    const link = document.createElement('a');
    link.href = fileUrl;
    link.setAttribute('download', 'sample.xlsx'); // Set the desired filename
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const closeUploadModal = () => {
    setState((prevState) => ({
      ...prevState,
      importModal: false,
      loader: false,
    }));
  };

  //  Import Care Library
  const addDocument = async () => {
    if (docRef?.current) {
      const uploadedDoc = await docRef.current.selectedDocument();
      if (uploadedDoc) {
        const file = uploadedDoc.document;
        const formData = new FormData();
        formData.append('module', 'Care');
        formData.append('type', activeCareType);
        formData.append('xls', file);
        setState((prevState) => ({
          ...prevState,
          loader: true,
        }));
        const response = await dispatch(action.importCareLibrary(formData));

        closeUploadModal();
        if (response.code === 200) {
          setCareLabelState((prevState) => ({
            ...prevState,
            CareLabelList: [],
            selectedLibrary: [],
            pageIndex: 1,
            hasMore: false,
            totalCount: 0,
          }));
          await getCurrentLibraryData();
          if (response.data?.logErr?.length > 0) {
            setState((prevState) => ({
              ...prevState,
              errorLog: response.data.logErr,
              isErrorShow: true,
            }));
          }
        }
      }
    }
  };

  return (
    <>
      <div className="page-title-filter sticky">
        <div className="title-container d-flex align-items-center">
          <h4 className="title">{t('CARE_LABELS.CARE_LIST.CARE_LABEL_LIBRARY')}</h4>
        </div>
        <LibraryFilter
          importSampleFile={importSampleFile}
          downloadSampleFile={downloadSampleFile}
          handleAddLibrary={handleAddLibrary}
          selectedLibrary={selectedLibrary}
          handleDeleteMultiple={handleDeleteMultiple}
          handleCheckAll={handleCheckAll}
          isallChecked={isallChecked}
          handleLibrarySearchText={handleLibrarySearchText}
          handleSearchSubmit={handleSearchSubmit}
          searchText={libSearchText}
          CareAddEditAccess={CareAddEditAccess}
          CareDeleteAccess={CareDeleteAccess}
          setCareLabelState={setCareLabelState}
        />
      </div>

      <Container fluid>
        <div className="tabs-container">
          <div className="tab-left">
            <ul>
              {checkArray(careTypeTabs) &&
                careTypeTabs.map((ele) => {
                  return (
                    <li key={ele.label} className={ele.isActive && 'active-menu'}>
                      <Link className="cursor-pointer text-dark text-decoration-none" to={`/libraries/care/${ele.id}`}>
                        {ele.label}
                      </Link>
                    </li>
                  );
                })}
            </ul>
          </div>
          <div className="tab-right">
            <Link className={`${pageType === 'Library' && 'active'} btn btn-default text-dark text-decoration-none mr-2`} to="/libraries/care/symbol">
              {t('CARE_LABELS.CARE_LIST.CARE_LABEL_LIBRARY')}
            </Link>
            <Link className={`${pageType === 'Group' && 'active'} btn btn-default text-dark text-decoration-none`} to="/libraries/care-group">
              {t('CARE_LABELS.CARE_LIST.CARE_LABEL_GROUPS')}
            </Link>
          </div>
        </div>
        <div>
          {careType === 'symbol' && <SymbolTable handleDelete={handleDelete} CareAddEditAccess={CareAddEditAccess} CareDeleteAccess={CareDeleteAccess} />}
          {(careType === 'instruction' || careType === 'disclaimer') && (
            <InstructionDisclaimerTable handleDelete={handleDelete} CareAddEditAccess={CareAddEditAccess} CareDeleteAccess={CareDeleteAccess} />
          )}
        </div>
      </Container>

      {isErrorShow && <ErrorLogModal show={isErrorShow} log={errorLog} handleClose={() => handleCloseError()} />}
      {deleteModal && (
        <ConfirmationPopup
          onOpen={deleteModal}
          onConfirm={() => confirmDelete()}
          title={`${t('CARE_LABELS.CARE_LIST.DELETE_CARE_LABEL')} ${activeCareType}`}
          message={`${t('CARE_LABELS.CARE_LIST.DELETE_MESSAGE')} ${activeCareType} from the care label library?`}
          toggle={() => handleCloseDeleteModal()}
          // loading={isLoadingConf}
        />
      )}
      {deleteMultipleModal && (
        <ConfirmationPopup
          onOpen={deleteMultipleModal}
          onConfirm={() => confirmDeleteMultiple()}
          title={t('CARE_LABELS.CARE_LIST.DELETE_CARE_LABELS')}
          message={t('CARE_LABELS.CARE_LIST.SELECTED_CARE_LABEL_MESSAGE')}
          toggle={() => handleCloseDeleteModal()}
        />
      )}
      {importModal && (
        <ImportModal showUploadModal={importModal} closeUploadModal={closeUploadModal} docRef={docRef} addDocument={addDocument} loader={loader} />
      )}
    </>
  );
}

export default List;
